import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import "./ChromaGrid.css";

const DEFAULT_RADIUS = 280;
const DEFAULT_DAMPING = 0.45;
const DEFAULT_FADE_OUT = 0.6;
const DEFAULT_EASE = "power3.out";

export const ChromaGrid = ({
  children,
  className = "",
  radius = DEFAULT_RADIUS,
  damping = DEFAULT_DAMPING,
  fadeOut = DEFAULT_FADE_OUT,
  ease = DEFAULT_EASE,
  disableAnimations = false,
}) => {
  const rootRef = useRef(null);
  const fadeRef = useRef(null);
  const setX = useRef(null);
  const setY = useRef(null);
  const pos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const el = rootRef.current;
    if (disableAnimations || !el) return;

    setX.current = gsap.quickSetter(el, "--x", "px");
    setY.current = gsap.quickSetter(el, "--y", "px");
    const { width, height } = el.getBoundingClientRect();
    pos.current = { x: width / 2, y: height / 2 };
    setX.current(pos.current.x);
    setY.current(pos.current.y);
  }, [disableAnimations]);

  const moveTo = (x, y) => {
    gsap.to(pos.current, {
      x,
      y,
      duration: damping,
      ease,
      onUpdate: () => {
        setX.current?.(pos.current.x);
        setY.current?.(pos.current.y);
      },
      overwrite: true,
    });
  };

  const handleMove = (e) => {
    if (disableAnimations || !rootRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    moveTo(e.clientX - r.left, e.clientY - r.top);
    gsap.to(fadeRef.current, { opacity: 0, duration: 0.25, overwrite: true });
  };

  const handleLeave = () => {
    if (disableAnimations) return;
    gsap.to(fadeRef.current, { opacity: 1, duration: fadeOut, overwrite: true });
  };

  return (
    <div
      ref={rootRef}
      className={`chroma-grid ${className}`}
      style={{ "--r": `${radius}px` }}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
    >
      {children}
      {!disableAnimations && (
        <>
          <div className="chroma-overlay" />
          <div ref={fadeRef} className="chroma-fade" />
        </>
      )}
    </div>
  );
};

export const ChromaCard = ({ children, className = "", testId, borderColor = "transparent", onClick }) => {
  return (
    <article
      data-testid={testId}
      className={`chroma-card ${className}`}
      onClick={onClick}
      style={{ "--card-border": borderColor, cursor: onClick ? "pointer" : "default" }}
    >
      {children}
    </article>
  );
};

export default ChromaGrid;
